import { useEffect } from 'react';
import { servicesData } from '../../data/servicesData';

const StructuredData = ({ type = 'homepage', service = null }) => {
  const baseUrl = 'https://solvedotlabs.com';

  useEffect(() => {
    // Remove existing structured data
    const existingScript = document.getElementById('structured-data');
    if (existingScript) {
      existingScript.remove();
    }

    let structuredData = {};

    if (type === 'homepage') {
      // Organization Schema with LocalBusiness
      structuredData = {
        '@context': 'https://schema.org',
        '@type': ['Organization', 'LocalBusiness', 'SoftwareApplication'],
        'name': 'SolveDot Labs',
        'alternateName': ['SolveDot', 'Solve Dot Labs', 'SolveDot Labs Pvt. Ltd.', 'SolveDotLabs.com'],
        'legalName': 'SolveDot Labs Pvt. Ltd.',
        'url': baseUrl,
        'logo': `${baseUrl}/icon-nobg.png`,
        'description': 'SolveDot Labs (SolveDot Labs Pvt. Ltd.) is a leading software development company specializing in AI solutions, website development, IoT systems, robotics, and custom software development. We deliver guaranteed high-quality, zero-downtime software solutions including AI chatbots, data analytics, web development, IoT systems, manufacturing solutions, CRM, and mobile apps.',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': 'Habitech Qube Crystal Mall, Knowledge Park 3',
          'addressLocality': 'Greater Noida',
          'addressRegion': 'Uttar Pradesh',
          'postalCode': '201308',
          'addressCountry': 'IN'
        },
        'geo': {
          '@type': 'GeoCoordinates',
          'latitude': '28.4744',
          'longitude': '77.5040'
        },
        'contactPoint': {
          '@type': 'ContactPoint',
          'telephone': '+91-7838443394',
          'contactType': 'Customer Service',
          'areaServed': ['IN', 'US', 'GB', 'CA', 'AU'],
          'availableLanguage': ['English', 'Hindi']
        },
        'sameAs': [
          'https://www.linkedin.com/company/solvedotlabs/',
          'https://x.com/solvedotlabs',
          'https://www.facebook.com/profile.php?id=61583182950566',
          'https://www.instagram.com/solvedotlabs/'
        ],
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.9',
          'reviewCount': '200'
        },
        'priceRange': '$$',
        'openingHours': 'Mo-Fr 09:00-18:00',
        'knowsAbout': [
          'AI Solutions',
          'Software Development',
          'Website Development',
          'IoT Systems',
          'Robotics',
          'Web Development',
          'Mobile App Development',
          'Data Analytics',
          'Manufacturing Software',
          'Industry 4.0'
        ],
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': 'Software Development Services',
          'itemListElement': [
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Service',
                'name': 'AI Solutions and Development',
                'description': 'AI chatbot development, machine learning solutions, and AI-powered software'
              }
            },
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Service',
                'name': 'Website Development',
                'description': 'Custom website development, web applications, and e-commerce solutions'
              }
            },
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Service',
                'name': 'IoT Systems',
                'description': 'Internet of Things solutions, IoT device integration, and smart systems'
              }
            },
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Service',
                'name': 'Robotics Solutions',
                'description': 'Robotics software development and automation solutions'
              }
            },
            {
              '@type': 'Offer',
              'itemOffered': {
                '@type': 'Service',
                'name': 'Software Development',
                'description': 'Custom software development, enterprise solutions, and software consulting'
              }
            }
          ]
        }
      };
    } else if (type === 'website') {
      // WebSite Schema
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': 'SolveDot Labs',
        'url': baseUrl,
        'potentialAction': {
          '@type': 'SearchAction',
          'target': {
            '@type': 'EntryPoint',
            'urlTemplate': `${baseUrl}/services?search={search_term_string}`
          },
          'query-input': 'required name=search_term_string'
        }
      };
    } else if (type === 'services' && !service) {
      // Service List Schema
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        'itemListElement': servicesData.map((svc, index) => ({
          '@type': 'ListItem',
          'position': index + 1,
          'item': {
            '@type': 'Service',
            'name': svc.title,
            'description': svc.description,
            'url': `${baseUrl}/services/${svc.id}`,
            'provider': {
              '@type': 'Organization',
              'name': 'SolveDot Labs'
            },
            'serviceType': svc.category
          }
        }))
      };
    } else if (type === 'service' && service) {
      // Individual Service Schema
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        'name': service.title,
        'description': service.description,
        'url': `${baseUrl}/services/${service.id}`,
        'provider': {
          '@type': 'Organization',
          'name': 'SolveDot Labs',
          'url': baseUrl
        },
        'serviceType': service.category,
        'areaServed': {
          '@type': 'Country',
          'name': 'India'
        },
        'hasOfferCatalog': {
          '@type': 'OfferCatalog',
          'name': service.title,
          'itemListElement': service.features.map((feature, index) => ({
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': feature
            }
          }))
        }
      };
    } else if (type === 'about') {
      // AboutPage Schema
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        'name': 'About SolveDot Labs',
        'description': 'Learn about SolveDot Labs - a software development company delivering guaranteed high-quality, zero-downtime software solutions.',
        'url': `${baseUrl}/about`,
        'mainEntity': {
          '@type': 'Organization',
          'name': 'SolveDot Labs',
          'foundingDate': '2020',
          'numberOfEmployees': {
            '@type': 'QuantitativeValue',
            'value': '50+'
          }
        }
      };
    } else if (type === 'contact') {
      // ContactPage Schema
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        'name': 'Contact SolveDot Labs',
        'description': 'Get in touch with SolveDot Labs for software development services, consultation, and support.',
        'url': `${baseUrl}/contact`
      };
    } else if (type === 'faq') {
      // FAQPage Schema
      structuredData = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': [
          {
            '@type': 'Question',
            'name': 'What is SolveDot Labs?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'SolveDot Labs (SolveDot Labs Pvt. Ltd.) is a leading software development company specializing in AI solutions, website development, IoT systems, robotics, and custom software development. We deliver guaranteed high-quality, zero-downtime software solutions.'
            }
          },
          {
            '@type': 'Question',
            'name': 'What services does SolveDot Labs offer?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'SolveDot Labs offers comprehensive software development services including AI solutions, website development, IoT systems, robotics solutions, mobile app development, data analytics, manufacturing software (MES, Industry 4.0), CRM development, and custom software development.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Where is SolveDot Labs located?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'SolveDot Labs is located in Greater Noida, Uttar Pradesh, India. We serve clients globally with our software development and IT services.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Does SolveDot Labs provide AI development services?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes, SolveDot Labs specializes in AI solutions including AI chatbot development, machine learning solutions, predictive analytics, and AI-powered software applications.'
            }
          },
          {
            '@type': 'Question',
            'name': 'What is SolveDot Labs uptime guarantee?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'SolveDot Labs guarantees 99% uptime for all our software solutions, ensuring your operations run smoothly 24/7 with almost zero downtime.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Does SolveDot Labs develop IoT and robotics solutions?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes, SolveDot Labs provides comprehensive IoT systems development and robotics solutions, including IoT device integration, smart systems, and robotics software development.'
            }
          },
          {
            '@type': 'Question',
            'name': 'Is SolveDot Labs a software development company?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': 'Yes, SolveDot Labs (SolveDot Labs Pvt. Ltd.) is a leading software development company based in Greater Noida, India, providing custom software development, website development, and enterprise software solutions.'
            }
          }
        ]
      };
    }

    // Add structured data script
    const script = document.createElement('script');
    script.id = 'structured-data';
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      const scriptToRemove = document.getElementById('structured-data');
      if (scriptToRemove) {
        scriptToRemove.remove();
      }
    };
  }, [type, service, baseUrl]);

  return null;
};

export default StructuredData;

